import { EMessageType } from '@cian/chats-api';

import { openEventChannel } from './openEventChannel';
import { trackUserStatus } from './tracking';
import { trackFileSend } from '../../containers/AttachFileButtonContainer/tracking';
import { trackUnarchivedButtonClick } from '../../containers/MessageInputFieldContainer/tracking';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { checkUsersSession } from '../../services/checkUsersSession';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TChatInListSchema } from '../../types/chatList';
import { EHostType } from '../../types/hostType';
import { EMessageFileTypes } from '../../types/messageFiles';
import { TMessageSchema } from '../../types/messages';
import { EMobileLayoutActiveSide } from '../../types/mobileSide';
import { TThunkAction } from '../../types/redux/thunk';
import { ERoute } from '../../types/route';
import { ETelemetryEvents } from '../../types/telemetry';
import { EChatRole } from '../../types/userData';
import { deepEqual } from '../../utils/deepEqual';
import { getUserStatusDiff } from '../../utils/getUserStatusDiff';
import { addIncomingMessage, closeChat } from '../chat/actions';
import { updateChatImages } from '../chatImages';
import { toggleChangeListActive, updatedFromServer } from '../chatList';
import { removeFromArchive } from '../chatOperations';
import { setWsSendSucceed } from '../files/actions';
import { setMobileActiveSide } from '../mobileSide';
import { changeRoute } from '../route';
import { setSendFirstMessageSuccess, setSendMessageSuccess } from '../sendMessage';
import { setUnreadMessagesCount } from '../unreadMessagesCount';
import { closeUserCard } from '../userCard/closeUserCard';
import { setUserStatus } from '../userStatus';
import { setWsError } from '../wsError';
import { IWsErrorEvent } from '../wsError/types';

export const listenEventChannel =
  (): TThunkAction<void> =>
  (dispatch, getState, { telemetry }) => {
    const {
      socket: { eventChannel },
    } = getState();

    const onMessageId = eventChannel.addIncommingMessageListener(({ data }) => {
      const state = getState();

      const {
        socket: { clientEvents },
        chatList,
        chatWindow,
        hostType,
        user,
      } = state;
      const { isChangeListActive, chats, currentRoute } = chatList;

      // tslint:disable-next-line:switch-default
      switch (data.type) {
        case 'session':
          // FIXME перенести в редюсер
          if (data.uid) {
            const clientEvent = clientEvents.get(data.uid);

            if (!clientEvent) {
              return;
            }

            clientEvents.set(data.uid, { ...clientEvent, status: ERequestApiStatus.Succeed });
          }

          /**
           * Событие сессии от сервера. Приходит в случаях:
           * 1. Когда мы регистрируем сессию по WS и нам приходит эхо события.
           * 2. Когда сессия истекла и сервер отправил событие.
           * 3. Сервер отправил событие ошибки с кодом sessionExpired.
           */
          if (data.session.expired) {
            dispatch(checkUsersSession());
          }
          telemetry.increment(ETelemetryEvents.ChatWsSessionSuccess);

          break;
        case 'chat':
          // После добавления custom actions нужно дополнительно проверять, что изменился статус чата (заблокирован/удален/заархивирован)
          const currentChat = chats.find(chat => chat.chatId === data.chat.chatId);

          const wasChangedChatCustomActions = currentChat
            ? !deepEqual(currentChat.customActions, data.chat.customActions)
            : false;
          const wasChangedChatStatus =
            currentChat &&
            (currentChat.archived !== data.chat.archived ||
              currentChat.blocked !== data.chat.blocked ||
              // @ts-expect-error TODO: обновить схему до V2
              data.chat.deletedV2);

          dispatch(updatedFromServer(data.chat as unknown as TChatInListSchema));

          // Если чатов нет, а изменение списка активно, то деактивировать изменение списка
          if (!chats.length && isChangeListActive) {
            dispatch(toggleChangeListActive());
          }
          if (wasChangedChatCustomActions && !wasChangedChatStatus && currentRoute === ERoute.Archive) {
            return;
          }

          if (
            chatWindow.currentOpenedChatId === data.chat.chatId &&
            // @ts-expect-error TODO: обновить схему до V2
            (data.chat.deletedV2 || data.chat.archived)
          ) {
            dispatch(closeUserCard());
          }

          // Если чат не в iFrame, был открыт и его удалили или перенесли в архив, закрыть его.
          // TODO: Подумать, что делать, если чат был в iFrame, и его удалили/архивировали из другого места
          if (
            hostType !== EHostType.Frame &&
            chatWindow.currentOpenedChatId === data.chat.chatId &&
            // @ts-expect-error TODO: обновить схему до V2
            (data.chat.deletedV2 || data.chat.archived)
          ) {
            dispatch(closeChat());
            dispatch(setMobileActiveSide(EMobileLayoutActiveSide.Left));

            return;
          }

          if (currentRoute === ERoute.Archive && chatWindow.currentOpenedChatId === data.chat.chatId) {
            dispatch(changeRoute({ route: ERoute.Messages, notClearCurrentOpenedChatId: true }));
          }

          if (!data.chat.message.uid) {
            return;
          }

          const event = clientEvents.get(data.chat.message.uid);

          /**
           * При отсылке сообщения c веб от сервера приходит 2 сообщения: chat и message.
           * В случае, если это исходящее сообщение, сообщение chat нужно игнорировать,
           * так как статус выставляет сообщение message.
           * Однако, стоит помнить, что с сервера приходят сообщения от других участников и
           * их надо добавлять, как входящие. Также статус сообщения может быть изменен на Прочитано
           */
          if (!event || (event.type === EWsClientEventType.Message && event.status !== ERequestApiStatus.Pending)) {
            dispatch(addIncomingMessage(data.chat.message as unknown as TMessageSchema));

            // Если пришло сообщение с фото - обновить последние изображения чата
            if (data.chat.message.files?.images?.length) {
              dispatch(updateChatImages({ chatId: data.chat.chatId as string }));
            }
          }

          break;
        case 'message':
          const { uid, message } = data;
          const { files, chatId, type, userId: messageUserId } = message;
          const { images = [], documents = [], imagesAsDocuments = [], fileTypes = [] } = files || {};

          const isNewChat = chatId && user?.userId === messageUserId && chatWindow.currentOpenedChatId === 'blob';
          const messageChat = chats.find(chat => chat.chatId === chatId);
          const opponent = messageChat?.users?.find(user => user.chatRole === EChatRole.Opponent);

          // При отправке первого исходящего сообщения по ручке обратно приходит вебсокет с этим сообщением
          if (isNewChat) {
            dispatch(setSendFirstMessageSuccess({ message: message as unknown as TMessageSchema }));

            return;
          }

          // Вебсокет message без data.uid приходит всегда, когда появляется новое сообщение.
          // Вебсокет message c data.uid приходит только отправителю, когда он отправил сообщение через WebSocket с этим uid.
          if (!uid) {
            return;
          }

          const clientEvent = clientEvents.get(uid);

          if (!clientEvent) {
            return;
          }

          clientEvents.set(uid, { ...clientEvent, status: ERequestApiStatus.Succeed });

          dispatch(setSendMessageSuccess({ message: message as unknown as TMessageSchema, messageId: uid }));

          // Если была отправка сообщения с файлами (фото, документ, фотофайл)
          if (type === EMessageType.TextAndFiles) {
            const fileUid = images[0]?.uid || documents[0]?.uid || imagesAsDocuments[0]?.uid;
            const fileType = fileTypes[0] as EMessageFileTypes;

            dispatch(setWsSendSucceed({ chatId: chatId as string, uid }));
            trackFileSend({
              fileType,
              chatId: chatId as string,
              status: 'success',
              fileId: fileUid,
              opponentId: opponent?.userId,
            });
          }

          if (currentRoute === ERoute.Archive && chatId) {
            dispatch(removeFromArchive(chatId));
            trackUnarchivedButtonClick(opponent?.userId);
          }

          telemetry.increment(ETelemetryEvents.ChatWsMessageSendSuccess);

          break;
        case 'status':
          if (data.status) {
            const currentChat = currentOpenChatSelector(state);
            const opponentId = currentChatOpponentIdSelector(state);
            const offer = currentChat?.offer;
            const statusDiffStr = getUserStatusDiff(data.status.lastActionDatetime);

            dispatch(setUserStatus(data.status));
            trackUserStatus(statusDiffStr, offer, opponentId);
            telemetry.increment(ETelemetryEvents.ChatWsUserStatusSuccess);
          }
          break;
        case 'unread':
          dispatch(setUnreadMessagesCount(data.unread));

          break;
        case 'error':
          // TODO: собрать кейсы, когда приходят события ошибки без uid'ов
          // В этом случае скорее всего нужно выкидывать исключение
          if (data.uid) {
            dispatch(setWsError(data as IWsErrorEvent));
          }
      }
    });

    const onCloseId = eventChannel.addEventChannelListener('close', () => {
      eventChannel.removeIncommingMessageListener(onMessageId as string);
      eventChannel.removeEventChannelListener('close', onCloseId);

      dispatch(openEventChannel());
    });
  };
